
import React, { useCallback, useState } from 'react';
import { Upload, FileText, Loader2, AlertCircle, X, Files } from 'lucide-react';

interface FileUploadProps {
  onFileProcess: (base64Array: string[]) => Promise<void>;
  isProcessing: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onFileProcess, isProcessing }) => {
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files) as File[];
    const validFiles: File[] = [];

    for (const file of newFiles) {
      if (file.type !== 'application/pdf') {
        setError("Some files were not PDFs and were ignored.");
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError("Some files exceeded the 10MB limit and were ignored.");
        continue;
      }
      validFiles.push(file);
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  }, []);

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleProcessClick = async () => {
    if (selectedFiles.length === 0) return;
    
    setError(null);
    const base64Promises = selectedFiles.map(file => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = (e.target?.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = () => reject("Failed to read file.");
        reader.readAsDataURL(file);
      });
    });

    try {
      const base64Array = await Promise.all(base64Promises);
      await onFileProcess(base64Array);
    } catch (err) {
      setError("An error occurred while reading the files.");
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-8 glass-panel rounded-3xl shadow-xl">
      <div className={`border-2 border-dashed border-indigo-200/60 rounded-2xl p-10 text-center hover:bg-white/40 transition-all relative bg-white/20 ${isProcessing ? 'pointer-events-none opacity-50' : ''}`}>
        <input
          type="file"
          accept="application/pdf"
          multiple
          onChange={handleFileChange}
          disabled={isProcessing}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10"
        />
        
        <div className="flex flex-col items-center justify-center space-y-4">
          {isProcessing ? (
            <>
              <div className="relative">
                <div className="absolute inset-0 bg-blue-400 rounded-full blur-xl opacity-20 animate-pulse"></div>
                <Loader2 className="w-16 h-16 text-indigo-600 animate-spin relative z-10" />
              </div>
              <div className="space-y-2">
                <p className="text-xl font-bold text-slate-700">Analyzing Documents...</p>
                <p className="text-sm text-slate-500">The AI is merging data from your {selectedFiles.length} files.</p>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-gradient-to-tr from-blue-100 to-indigo-50 rounded-full flex items-center justify-center shadow-inner mb-2">
                <Upload className="w-8 h-8 text-indigo-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-slate-800">Upload Business PDFs</h3>
                <p className="text-slate-500 font-medium">Drag & drop or click to select multiple PDF files</p>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedFiles.length > 0 && !isProcessing && (
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between px-1">
            <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-2">
              <Files className="w-4 h-4" /> Selected Files ({selectedFiles.length})
            </h4>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {selectedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-white/60 backdrop-blur-sm border border-white/50 rounded-xl shadow-sm transition-transform hover:scale-[1.01]">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="bg-indigo-100 p-1.5 rounded-lg">
                    <FileText className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  </div>
                  <span className="text-sm text-slate-700 font-semibold truncate">{file.name}</span>
                  <span className="text-[10px] text-slate-400 font-mono bg-slate-100 px-1.5 rounded">{(file.size / 1024).toFixed(0)} KB</span>
                </div>
                <button 
                  onClick={() => removeFile(idx)}
                  className="p-1.5 hover:bg-red-100 text-slate-400 hover:text-red-500 rounded-lg transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={handleProcessClick}
            className="w-full mt-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-bold text-lg shadow-xl shadow-indigo-500/20 transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            Process {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''} with AI
          </button>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50/90 border border-red-100 text-red-700 rounded-xl flex items-center gap-2 backdrop-blur-sm shadow-sm">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">{error}</span>
        </div>
      )}
      
      <div className="mt-6 text-center">
        <p className="text-xs text-slate-400 font-medium">Supported formats: PDF (Max 10MB per file)</p>
      </div>
    </div>
  );
};
